// Iter 292 — Held email outbox. Everything the kill switch would have dropped
// waits here: preview, send (once delivery is live or the recipients are
// allowlisted), or remove — removal works while still paused.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Inbox, Loader2, Paperclip, Send, Trash2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const errText = (e, fallback) => formatApiErrorDetail(e?.response?.data?.detail) || fallback;

function PreviewDialog({ id, onClose }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (!id) return;
    setDoc(null);
    api.get(`/settings/email-outbox/${id}`).then(({ data }) => setDoc(data)).catch(() => toast.error("Could not load this email"));
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="email-outbox-preview">
        <DialogHeader>
          <DialogTitle className="text-base">{doc?.subject || "Held email"}</DialogTitle>
        </DialogHeader>
        {!doc ? <Loader2 className="h-5 w-5 animate-spin text-slate-300" /> : (
          <div className="space-y-2 text-xs text-slate-600">
            <div><span className="text-slate-400">To</span> {(doc.to || []).join(", ")} · <span className="text-slate-400">From</span> {doc.from}</div>
            <div><span className="text-slate-400">Held</span> {when(doc.held_at)}{doc.template && <> · <span className="text-slate-400">template</span> {doc.template}</>}
              {doc.pause_reason && <> · <span className="italic text-slate-400">reason: {doc.pause_reason}</span></>}</div>
            {!!doc.attachments?.length && (
              <div className="flex flex-wrap gap-1.5">{doc.attachments.map((a) => (
                <span key={a.filename} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5"><Paperclip className="h-3 w-3" /> {a.filename} · {kb(a.size)}</span>
              ))}</div>
            )}
            {doc.html
              ? <iframe title="Held email preview" srcDoc={doc.html} sandbox="" className="h-[60vh] w-full rounded border border-slate-200 bg-white" data-testid="email-outbox-preview-frame" />
              : <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 text-[12px]">{doc.text}</pre>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function EmailOutboxPanel({ paused, canEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState("");
  const [confirm, setConfirm] = useState(null);   // {kind: "discard"|"discard-all"|"send-all", id?}

  const load = useCallback(() => api.get("/settings/email-outbox", { params: { status: "held", limit: 200 } })
    .then(({ data }) => setData(data)).catch(() => toast.error("Could not load the outbox")), []);
  useEffect(() => { load(); }, [load]);

  const done = async (msg) => { if (msg) toast.success(msg); await load(); onChanged?.(); };
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errText(e, "Action failed")); } finally { setBusy(""); } };

  const sendOne = (row) => run(`send-${row.id}`, async () => {
    const { data: r } = await api.post(`/settings/email-outbox/${row.id}/send`);
    await done(r.status === "sent" ? `Sent to ${(row.to || []).join(", ")}` : "Everyone on this email has opted out — discarded");
  });
  const discardOne = (id) => run(`discard-${id}`, async () => { await api.delete(`/settings/email-outbox/${id}`); await done("Removed from the outbox"); });
  const sendAll = () => run("send-all", async () => {
    const { data: r } = await api.post("/settings/email-outbox/send-all");
    await done(`${r.sent} sent${r.failed ? `, ${r.failed} failed (still held)` : ""}${r.discarded ? `, ${r.discarded} opted out` : ""}`);
  });
  const discardAll = () => run("discard-all", async () => { const { data: r } = await api.post("/settings/email-outbox/discard-all"); await done(`${r.discarded} email(s) removed`); });

  const items = data?.items || [];
  const held = data?.counts?.held || 0;
  return (
    <div className="rounded-md border border-orange-200 bg-white/70 p-3 space-y-2" data-testid="email-outbox-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-orange-800 flex items-center gap-1.5">
          <Inbox className="h-3.5 w-3.5" /> Held outbox · {held} email{held === 1 ? "" : "s"}
        </div>
        {canEdit && held > 0 && (
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" disabled={!!busy || paused} onClick={() => setConfirm({ kind: "send-all" })}
                    title={paused ? "Resume delivery first" : "Send every held email now"}
                    className="h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="email-outbox-send-all">
              {busy === "send-all" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send all
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setConfirm({ kind: "discard-all" })}
                    className="h-7 border-orange-300 text-orange-800 hover:bg-orange-50" data-testid="email-outbox-discard-all">
              {busy === "discard-all" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Remove all
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-orange-900/80 leading-relaxed">
        Every send dropped by the pause is kept here with its full content. {paused
          ? "While paused you can preview or remove them; sending unlocks once delivery resumes (or when every recipient is on the QA allowlist)."
          : "Delivery is live — send what still matters, remove the rest."}
      </p>
      {!data ? <Loader2 className="h-4 w-4 animate-spin text-orange-300" /> : items.length === 0 ? (
        <div className="text-xs italic text-orange-800/70" data-testid="email-outbox-empty">Nothing held.</div>
      ) : (
        <ul className="divide-y divide-orange-100 rounded border border-orange-100 bg-white" data-testid="email-outbox-list">
          {items.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs" data-testid={`email-outbox-row-${row.id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-slate-800" title={row.subject}>{row.subject || "(no subject)"}</div>
                <div className="truncate text-slate-500">
                  to {(row.to || []).join(", ")} · held {when(row.held_at)}{row.template && ` · ${row.template}`} · {kb(row.size_bytes)}
                  {!!row.attachment_names?.length && <> · <Paperclip className="inline h-3 w-3" /> {row.attachment_names.length}</>}
                  {row.last_error && <span className="ml-1 text-rose-600" data-testid={`email-outbox-error-${row.id}`}>· {row.last_error}</span>}
                </div>
              </div>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setPreview(row.id)} data-testid={`email-outbox-preview-${row.id}`}>
                <Eye className="h-3.5 w-3.5" /> Preview
              </Button>
              {canEdit && (
                <>
                  <Button size="sm" variant="outline" className="h-7 border-emerald-300 px-2 text-emerald-700 hover:bg-emerald-50"
                          disabled={!!busy || paused} title={paused ? "Resume delivery first" : "Send now"}
                          onClick={() => sendOne(row)} data-testid={`email-outbox-send-${row.id}`}>
                    {busy === `send-${row.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-orange-700 hover:bg-orange-50 hover:text-orange-900"
                          disabled={!!busy} onClick={() => setConfirm({ kind: "discard", id: row.id })} data-testid={`email-outbox-discard-${row.id}`}>
                    {busy === `discard-${row.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Remove
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <PreviewDialog id={preview} onClose={() => setPreview("")} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="email-outbox-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "send-all" ? `Send all ${held} held email${held === 1 ? "" : "s"}?`
                : confirm?.kind === "discard-all" ? `Remove all ${held} held email${held === 1 ? "" : "s"}?` : "Remove this email from the outbox?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "send-all" ? "They go to their real recipients right now, oldest first. Anything that fails stays in the outbox."
                : "It will never be delivered. The record is kept for 30 days in the activity log, but the content cannot be sent afterwards."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="email-outbox-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="email-outbox-confirm-ok"
              className={confirm?.kind === "send-all" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-orange-600 hover:bg-orange-700"}
              onClick={() => { const c = confirm; setConfirm(null); if (c?.kind === "send-all") sendAll(); else if (c?.kind === "discard-all") discardAll(); else if (c) discardOne(c.id); }}>
              {confirm?.kind === "send-all" ? "Send all" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Resume confirmation — with the send / remove / keep choice for the held outbox.
export function ResumeEmailDialog({ open, heldCount, busy, onCancel, onConfirm }) {
  const [action, setAction] = useState("keep");
  useEffect(() => { if (open) setAction(heldCount > 0 ? "send" : "keep"); }, [open, heldCount]);
  const opts = [
    ["send", `Send the ${heldCount} held email${heldCount === 1 ? "" : "s"} now`, "They go to their real recipients immediately, oldest first."],
    ["keep", "Keep them in the outbox", "Decide one by one afterwards — nothing is sent yet."],
    ["discard", "Remove them all", "None of them will ever be delivered."],
  ];
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="email-resume-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Resume outbound email?</AlertDialogTitle>
          <AlertDialogDescription>
            Every welcome invite, password reset, commission summary and notification starts sending again immediately.
            Confirm only after templates and recipients look right.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {heldCount > 0 && (
          <div className="space-y-1.5" data-testid="email-resume-held-choice">
            <div className="text-xs font-semibold text-slate-700">{heldCount} email{heldCount === 1 ? " was" : "s were"} held while paused:</div>
            {opts.map(([v, label, hint]) => (
              <label key={v} className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm ${action === v ? "border-emerald-400 bg-emerald-50/60" : "border-slate-200"}`}>
                <input type="radio" name="held_action" value={v} checked={action === v} onChange={() => setAction(v)} className="mt-1" data-testid={`email-resume-${v}`} />
                <span><span className="font-medium text-slate-800">{label}</span><br /><span className="text-xs text-slate-500">{hint}</span></span>
              </label>
            ))}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="email-resume-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" onClick={(e) => { e.preventDefault(); onConfirm(heldCount > 0 ? action : "keep"); }}
                             data-testid="email-resume-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resume email"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
